/**
 * Utility functions shared across graph and explorer components
 * Adapted from Quartz-4
 */

import type { Color } from './types';

// ============================================================================
// CSS Variable Helpers
// ============================================================================

/**
 * Get a CSS custom property value from the document root
 * @param name CSS variable name (with or without --)
 * @returns Trimmed CSS variable value
 */
export function getCSSVariable(name: string): string {
  const varName = name.startsWith('--') ? name : `--${name}`;
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
}

/**
 * Get color from CSS variable with fallback
 * @param varName CSS variable name
 * @param fallback Fallback color if variable not found
 * @returns Color string
 */
export function getColor(varName: string, fallback: string = '#000000'): Color {
  const value = getCSSVariable(varName);
  return value || fallback;
}

// ============================================================================
// LocalStorage Helpers
// ============================================================================

/**
 * Safely get an item from localStorage
 * @param key Storage key
 * @returns Stored value or null if not found/error
 */
export function getLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn('localStorage access failed:', e);
    return null;
  }
}

/**
 * Safely set an item in localStorage
 * @param key Storage key
 * @param value Value to store
 * @returns true if successful, false otherwise
 */
export function setLocalStorage(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('localStorage write failed:', e);
    return false;
  }
}

/**
 * Safely parse JSON from localStorage
 * @param key Storage key
 * @param fallback Fallback value if parse fails
 * @returns Parsed object or fallback
 */
export function getLocalStorageJSON<T>(key: string, fallback: T): T {
  const value = getLocalStorage(key);
  if (!value) return fallback;

  try {
    return JSON.parse(value) as T;
  } catch (e) {
    console.warn('JSON parse failed:', e);
    return fallback;
  }
}

/**
 * Safely stringify and store JSON in localStorage
 * @param key Storage key
 * @param value Object to store
 * @returns true if successful, false otherwise
 */
export function setLocalStorageJSON<T>(key: string, value: T): boolean {
  try {
    const json = JSON.stringify(value);
    return setLocalStorage(key, json);
  } catch (e) {
    console.warn('JSON stringify failed:', e);
    return false;
  }
}

// ============================================================================
// Fetch Helpers
// ============================================================================

/**
 * Fetch JSON data with error handling
 * @param url URL to fetch
 * @returns Promise resolving to parsed JSON
 */
export async function fetchJSON<T>(url: string): Promise<T> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch ${url}:`, error);
    throw error;
  }
}

// ============================================================================
// DOM Helpers
// ============================================================================

/**
 * Safely query selector with type assertion
 * @param selector CSS selector
 * @param parent Parent element (default: document)
 * @returns Element or null
 */
export function querySelector<T extends Element = Element>(
  selector: string,
  parent: Document | Element = document
): T | null {
  return parent.querySelector<T>(selector);
}

/**
 * Query selector that throws if element not found
 * @param selector CSS selector
 * @param parent Parent element (default: document)
 * @returns Element
 * @throws Error if element not found
 */
export function requireElement<T extends Element = Element>(
  selector: string,
  parent: Document | Element = document
): T {
  const element = querySelector<T>(selector, parent);
  if (!element) {
    throw new Error(`Required element not found: ${selector}`);
  }
  return element;
}

/**
 * Wait for DOM to be ready
 * @returns Promise that resolves when DOM is ready
 */
export function waitForDOM(): Promise<void> {
  return new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    } else {
      resolve();
    }
  });
}

// ============================================================================
// Number Helpers
// ============================================================================

/**
 * Clamp a number between min and max
 * @param value Value to clamp
 * @param min Minimum value
 * @param max Maximum value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation between two values
 * @param a Start value
 * @param b End value
 * @param t Interpolation factor (0-1)
 * @returns Interpolated value
 */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Map a value from one range to another
 * @param value Input value
 * @param inMin Input range minimum
 * @param inMax Input range maximum
 * @param outMin Output range minimum
 * @param outMax Output range maximum
 * @returns Mapped value
 */
export function mapRange(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
): number {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

// ============================================================================
// String Helpers
// ============================================================================

/**
 * Slugify a string (convert to URL-safe format)
 * @param str Input string
 * @returns Slugified string
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Truncate a string to a maximum length
 * @param str Input string
 * @param maxLength Maximum length
 * @param suffix Suffix to append if truncated (default: '...')
 * @returns Truncated string
 */
export function truncate(str: string, maxLength: number, suffix: string = '...'): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

// ============================================================================
// Debounce & Throttle
// ============================================================================

/**
 * Debounce a function call
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (this: any, ...args: Parameters<T>) {
    const context = this;

    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      func.apply(context, args);
      timeout = null;
    }, wait);
  };
}

/**
 * Throttle a function call
 * @param func Function to throttle
 * @param limit Time limit in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

// ============================================================================
// Configuration Helpers
// ============================================================================

/**
 * Parse configuration from data attribute
 * @param element Element with data-config attribute
 * @param fallback Fallback configuration
 * @returns Parsed configuration merged with fallback
 */
export function parseConfig<T extends Record<string, any>>(
  element: Element,
  fallback: T
): T {
  const configStr = element.getAttribute('data-config');
  if (!configStr) return fallback;

  try {
    const parsed = JSON.parse(configStr);
    return { ...fallback, ...parsed };
  } catch (e) {
    console.warn('Failed to parse data-config:', e);
    return fallback;
  }
}

// ============================================================================
// Browser Feature Detection
// ============================================================================

/**
 * Check if WebGL is supported
 * @returns true if WebGL is supported
 */
export function isWebGLSupported(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
}

/**
 * Check if touch device
 * @returns true if device supports touch
 */
export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}
